<template>
  <div class="products-page">
    <!-- Hero Section with Background -->
    <section class="products-hero">
      <div class="hero-bg"></div>
      <div class="hero-content">
        <h1 class="hero-title">产品中心</h1>
      </div>
    </section>

    <!-- Main Content -->
    <section class="products-main">
      <div class="container">
        <!-- 产品分类循环 -->
        <div v-for="category in productCategories" :key="category.id" :id="`category-${category.id}`" class="category-section">
          <!-- Category Header -->
          <div class="products-header">
            <h2 class="series-title">{{ category.categoryName }}</h2>
            <p class="series-description">{{ category.categoryDesc }}</p>
          </div>

          <!-- Category Image/Video Section -->
          <div class="video-section" v-if="category.categoryUrl">
            <div class="video-placeholder">
              <img :src="getMediaUrl(category.categoryUrl)" :alt="category.categoryName" class="category-image" />
            </div>
          </div>

          <!-- Products Grid -->
          <div v-if="categoryProducts[category.id] && categoryProducts[category.id].length > 0" class="products-grid">
            <div class="product-card" v-for="product in categoryProducts[category.id]" :key="product.id" @click="handleProductClick(product)">
              <div class="product-image">
                <img :src="product.image" :alt="product.name" />
              </div>
              <div class="product-info">
                <h3 class="product-title">
                  <span class="product-series">{{ category.categoryName }}<span v-if="product.specs">-{{ product.specs }}</span></span>
                  <span class="product-name-red">{{ product.name }}</span>
                </h3>
                <!-- 产品标签 -->
                <div class="product-tags" v-if="product.tags && product.tags.length > 0">
                  <span class="product-tag" v-for="(tag, tagIndex) in product.tags" :key="tagIndex">
                    {{ tag }}
                  </span>
                </div>
                <div class="product-actions">
                  <a @click.stop="handleProductDetail(product)" class="product-link">
                    了解更多 
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                      <path d="M5 12L10 8L5 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </a>
                  <a @click.stop="handleProductBuy(product)" class="product-link">
                    购买
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                      <path d="M5 12L10 8L5 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <!-- No Products Message -->
          <div v-else-if="!loading" class="no-products">
            暂无产品数据
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-message">
          加载中...
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'Products',
  data() {
    return {
      productCategories: [], // 产品分类列表
      categoryProducts: {}, // 每个分类下的产品列表
      loading: false,
      productBackgrounds: [ // 产品背景色数组
        'linear-gradient(135deg, #D4A574 0%, #B8956A 100%)',
        'linear-gradient(135deg, #4A7C96 0%, #386A84 100%)',
        'linear-gradient(135deg, #8B7355 0%, #6B5A45 100%)',
        'linear-gradient(135deg, #C74545 0%, #A73535 100%)',
        'linear-gradient(135deg, #6B7AA1 0%, #525F7E 100%)',
        'linear-gradient(135deg, #7C9B7C 0%, #5E795E 100%)',
        'linear-gradient(135deg, #9B7C9B 0%, #795E79 100%)',
        'linear-gradient(135deg, #A1816B 0%, #7E6552 100%)'
      ]
    };
  },
  async mounted() {
    // 获取产品分类和产品数据
    await this.fetchProductData();
    
    // 检查是否有 categoryId 参数
    const categoryId = this.$route.query.categoryId;
    if (categoryId) {
      // 滚动到指定分类
      this.$nextTick(() => {
        this.scrollToCategory(categoryId);
      });
    } else {
      // 没有参数则滚动到顶部
      window.scrollTo(0, 0);
    }
  },
  watch: {
    // 监听路由变化
    '$route.query.categoryId'(newCategoryId) {
      if (newCategoryId) {
        this.$nextTick(() => {
          this.scrollToCategory(newCategoryId);
        });
      }
    }
  },
  methods: {
    // 获取媒体(图片)完整URL
    getMediaUrl(url) {
      const baseUrl = process.env.VUE_APP_IMAGE_URL || 'http://45.192.98.104:8080';
      return baseUrl + url;
    },

    // 获取产品背景色
    getProductBackground(index) {
      return this.productBackgrounds[index % this.productBackgrounds.length];
    },

    // 获取产品分类和产品数据
    async fetchProductData() {
      try {
        this.loading = true;
        
        // 1. 获取产品分类
        const categoryRes = await this.$api.product.getProductCategoryList();
        if (categoryRes.code === 200 && categoryRes.data) {
          this.productCategories = categoryRes.data;
          
          // 2. 获取每个分类下的产品
          for (const category of this.productCategories) {
            await this.fetchCategoryProducts(category.id);
          }
        }
      } catch (error) {
        console.error('获取产品数据失败:', error);
      } finally {
        this.loading = false;
      }
    },

    // 获取指定分类的产品
    async fetchCategoryProducts(categoryId) {
      try {
        const res = await this.$api.product.getProducts({
          publishLocation: 2, // 产品中心页面
          productCategory: categoryId
          // API不支持分页，一次性返回所有数据
        });
        
        if (res.code === 200 && res.data) {
          const products = res.data.map(item => ({
            id: item.id,
            name: item.productName,
            model: item.productCarType || '新品',
            specs: item.productSellingPoints,
            image: item.productImage ? this.getMediaUrl(item.productImage) : require('../assets/images/product.png'),
            jumpUrl: item.jumpUrl,
            tags: item.tags || [] // 产品标签数组
          }));
          
          this.$set(this.categoryProducts, categoryId, products);
        }
      } catch (error) {
        console.error(`获取分类${categoryId}的产品失败:`, error);
        this.$set(this.categoryProducts, categoryId, []);
      }
    },

    // 处理产品点击
    handleProductClick(product) {
      if (product.jumpUrl) {
        window.open(product.jumpUrl, '_blank');
      } else {
        this.$router.push(`/product/${product.id}`);
      }
    },

    // 处理产品详情链接
    handleProductDetail(product) {
      this.$router.push(`/product/${product.id}`);
    },

    // 处理产品购买链接
    handleProductBuy(product) {
      if (product.jumpUrl) {
        window.open(product.jumpUrl, '_blank');
      }
    },

    // 滚动到指定分类
    scrollToCategory(categoryId) {
      const element = document.getElementById(`category-${categoryId}`);
      if (element) {
        // 获取元素距离页面顶部的距离
        const elementTop = element.offsetTop;
        // 减去导航栏高度（80px）和一些额外的间距（20px）
        const offset = 100;
        window.scrollTo({
          top: elementTop - offset,
          behavior: 'smooth'
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.products-page {
  min-height: 100vh;
  background: #fff;
}

/* Hero Section */
.products-hero {
  position: relative;
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-top: 80px; // Account for fixed header

  @media (max-width: 768px) {
    height: 250px;
    margin-top: 60px;
  }

  .hero-bg {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('../assets/images/header-bg.png') center/cover no-repeat;
    filter: brightness(0.7);
  }

  .hero-content {
    position: relative;
    z-index: 2;
    text-align: center;
  }

  .hero-title {
    font-size: 48px;
    font-weight: bold;
    color: #fff;
    margin: 0;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

    @media (max-width: 768px) {
      font-size: 32px;
    }
  }
}

/* Main Content */
.products-main {
  padding: 80px 0;
  background: #f8f9fa;

  @media (max-width: 768px) {
    padding: 40px 0;
  }

  .container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 40px;

    @media (max-width: 768px) {
      padding: 0 20px;
    }
  }
}

/* Products Header */
.products-header {
  text-align: center;
  margin-bottom: 60px;

  .series-title {
    font-size: 36px;
    font-weight: bold;
    color: #333;
    margin-bottom: 30px;
    position: relative;
    display: inline-block;

    &::after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 50%;
      transform: translateX(-50%);
      width: 60px;
      height: 3px;
      background: #ed1c24;
    }

    @media (max-width: 768px) {
      font-size: 28px;
    }
  }

  .series-description {
    font-size: 16px;
    line-height: 1.8;
    color: #666;
    max-width: 900px;
    margin: 0 auto;

    @media (max-width: 768px) {
      font-size: 14px;
      line-height: 1.6;
    }
  }
}

/* Category Section */
.category-section {
  margin-bottom: 120px;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    margin-bottom: 80px;
  }
}

/* Video Section */
.video-section {
  margin-bottom: 80px;

  @media (max-width: 768px) {
    margin-bottom: 40px;
  }

  .video-placeholder {
    position: relative;
    width: 100%;
    max-width: 1720px;
    margin: 0 auto;
    aspect-ratio: 1720 / 640;
    background: url('../assets/images/media-center.png') center/cover no-repeat;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition: transform 0.3s ease;

    &:hover {
      transform: scale(1.01);

      .play-button svg {
        transform: scale(1.1);
      }
    }

    .play-button {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 2;
      
      svg {
        transition: transform 0.3s ease;
        filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
      }
    }

      video {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }

      .category-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
  }
}

/* Products Grid */
.products-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  margin-bottom: 60px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }

  .product-card {
    background: #fff;
    overflow: hidden;
    transition: all 0.3s ease;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
    cursor: pointer;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
    }

    .product-image {
      position: relative;
      width: 100%;
      height: 320px;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      padding: 20px;

      @media (max-width: 768px) {
        height: 200px;
        padding: 15px;
      }

      img {
        width: 200px;
        height: 260px;
        object-fit: contain;
        transition: transform 0.3s ease;
        z-index: 1;
        position: relative;

        @media (max-width: 768px) {
          width: 100%;
          height: 100%;
          max-width: 140px;
          max-height: 170px;
        }
      }

      &:hover img {
        transform: scale(1.05);
      }
    }

    .product-info {
      padding: 20px;
      background: #fff;
      text-align: left;

      @media (max-width: 768px) {
        padding: 12px;
      }

      .product-title {
        margin-bottom: 12px;
        line-height: 1.4;
        text-align: left;
        display: flex;
        align-items: baseline;
        gap: 8px;
        flex-wrap: wrap;

        @media (max-width: 768px) {
          margin-bottom: 8px;
          gap: 4px;
        }

        .product-series {
          font-size: 14px;
          font-weight: 500;
          color: #333;

          @media (max-width: 768px) {
            font-size: 11px;
          }
        }

        .product-name-red {
          font-size: 18px;
          font-weight: bold;
          color: #ed1c24;

          @media (max-width: 768px) {
            font-size: 14px;
          }
        }
      }
      
      .product-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 16px;

        @media (max-width: 768px) {
          gap: 5px;
          margin-bottom: 10px;
        }
        
        .product-tag {
          display: inline-block;
          padding: 4px 10px;
          background: #f5f5f5;
          color: #666;
          font-size: 12px;
          border-radius: 3px;
          border: 1px solid #e8e8e8;
          transition: all 0.2s ease;

          @media (max-width: 768px) {
            padding: 3px 8px;
            font-size: 10px;
          }
          
          &:hover {
            background: #ed1c24;
            color: #fff;
            border-color: #ed1c24;
          }
        }
      }

      .product-actions {
        display: flex;
        gap: 20px;
        justify-content: flex-start;

        @media (max-width: 768px) {
          gap: 12px;
        }

        .product-link {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: #666;
          font-size: 13px;
          font-weight: 500;
          text-decoration: none;
          transition: all 0.3s ease;

          @media (max-width: 768px) {
            font-size: 11px;
            gap: 4px;
          }

          svg {
            transition: transform 0.3s ease;
            width: 12px;
            height: 12px;

            @media (max-width: 768px) {
              width: 10px;
              height: 10px;
            }
          }

          &:hover {
            color: #ed1c24;

            svg {
              transform: translateX(3px);
            }
          }
        }
      }
    }
  }
}

/* Load More Button */
.load-more {
  text-align: center;
  margin-top: 60px;

  .load-more-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 0;
    background: transparent;
    border: none;
    color: #ed1c24;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      right: 0;
      height: 1px;
      background: #ed1c24;
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    &:hover {
      &::after {
        opacity: 1;
      }

      svg {
        animation: bounce 0.5s ease infinite;
      }
    }

    svg {
      width: 14px;
      height: 14px;
    }

    @keyframes bounce {
      0%, 100% {
        transform: translateY(0);
      }
      50% {
        transform: translateY(3px);
      }
    }
  }
}

/* Loading and No Products Messages */
.loading-message,
.no-products {
  text-align: center;
  padding: 60px 20px;
  color: #999;
  font-size: 16px;
}
</style>
